import { CellContext, ColumnMeta, Row } from '@tanstack/react-table';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useFlag } from '@unleash/proxy-client-react';
import { ResolvedCustomField } from '../../../domain/resolve-order-entry';
import {
  OrderColumnDeepFragment,
  PropertySetType,
  UnitSettingsType,
} from '../../../gql/graphql';
import {
  Substitution,
  getValidSubstitutions,
} from '../../../utils/formula-utils';
import { isEditableTableMeta } from '../../table/Table';
import { QuantityCell } from './DynamicQuantityCell';
import { TableEntry } from './OrderEntriesPanel';
import { Point } from 'src/domain/geometry/geometric-types';

type ActiveQuantityCell = {
  columnName: string;
  rowId: string;
} | null;

export type CustomColumnMeta = {
  handleActiveQuantityCellClick: (
    columnName: string,
    selectedEntry: Row<TableEntry>
  ) => void;
  orderColumns: OrderColumnDeepFragment[];
  columnName: string;
  propertySets: PropertySetType[];
  projectId: string;
  isDrawingQuantityColumn: boolean;
  activeQuantityCell: ActiveQuantityCell;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  };
};

function isCustomColumnMeta(
  meta: ColumnMeta<TableEntry, ResolvedCustomField> | undefined
): meta is CustomColumnMeta {
  if (!meta) {
    return false;
  }
  let casted = meta as CustomColumnMeta;
  return (
    !isNil(casted.handleActiveQuantityCellClick) &&
    !isNil(casted.isDrawingQuantityColumn) &&
    !isNil(casted.orderColumns) &&
    !isNil(casted.columnName) &&
    !isNil(casted.propertySets) &&
    !isNil(casted.projectId)
  );
}

function CustomColumnCell({
  column,
  row,
  table,
}: CellContext<TableEntry, string | undefined>): React.ReactNode {
  const columnMeta = column.columnDef.meta;
  const {
    options: { meta: tableMeta },
  } = table;
  invariant(isCustomColumnMeta(columnMeta));
  invariant(isEditableTableMeta(tableMeta));
  const {
    orderColumns,
    columnName,
    propertySets,
    projectId,
    handleActiveQuantityCellClick,
    activeQuantityCell,
    isDrawingQuantityColumn,
    projectSettings,
  } = columnMeta;
  const columnKey =
    `custom.${column.id}.quantity` as `custom.${string}.quantity`;
  const availableSubstitutions: Substitution[] = useMemo(
    () => getValidSubstitutions(column.id, orderColumns),
    [column.id, orderColumns]
  );

  const enableUnitSettings = useFlag('enable-unit-settings');

  const customField = row.original.customFields?.find(
    (column) => `custom.${column.orderColumnId}.quantity` === columnKey
  );

  return (
    <QuantityCell
      tableMeta={tableMeta}
      projectId={projectId}
      orderColumns={orderColumns}
      row={row}
      table={table}
      columnId={column.id}
      columnName={columnName}
      columnKey={columnKey}
      quantity={customField?.value ?? null}
      resolvedValue={customField?.resolvedValue ?? null}
      propertySets={propertySets}
      availableSubstitutions={availableSubstitutions}
      activeQuantityCell={activeQuantityCell}
      handleActiveQuantityCellClick={handleActiveQuantityCellClick}
      isDrawingQuantityColumn={isDrawingQuantityColumn}
      isSomePropertyMissing={!!customField?.isSomeQuantityMissing}
      projectSettings={{
        ...projectSettings,
        unitSettings: enableUnitSettings
          ? projectSettings.unitSettings
          : undefined,
      }}
    />
  );
}

export default CustomColumnCell;
